/**
 * LCFPIOperationalFlags.js — Realism Layer
 * Shows operational imperfections from SSOT — controlled friction that
 * builds institutional credibility. Real programmes always have gaps.
 */
import React, { useState, useEffect } from "react";
import client from "@/api/client";
import { AlertTriangle, AlertCircle, Info, CheckCircle2, Loader, ChevronDown, ChevronUp } from "lucide-react";

const SEVERITY_STYLES = {
  critical: { icon: AlertCircle, color: "#ef4444", bg: "#fff1f2", border: "#fecdd3", label: "CRITICAL" },
  warning:  { icon: AlertTriangle, color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: "FLAG" },
  info:     { icon: Info, color: "#1e40af", bg: "#eff6ff", border: "#bfdbfe", label: "NOTE" },
};

const HEALTH_COLORS = { Excellent:"#059669", Good:"#059669", "Needs Attention":"#d97706", "Critical Issues":"#ef4444" };

/**
 * Standalone operational flags panel for a single project.
 */
export function OperationalFlagsPanel({ projectId, compact = false }) {
  const [flagData, setFlagData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    if (!projectId) return;
    client.get(`/lcfpi/operational-flags/${projectId}`)
      .then(r => setFlagData(r.data)).catch(() => setFlagData(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="flex items-center gap-2 p-3"><Loader size={12} className="animate-spin text-slate-400"/><span className="text-xs text-slate-400">Checking operational status…</span></div>;
  if (!flagData) return null;
  if (flagData.flag_count === 0) {
    return (
      <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg p-2.5">
        <CheckCircle2 size={12} className="text-green-500"/>
        <span className="text-xs text-green-700 font-medium">No operational flags — all checks passed</span>
      </div>
    );
  }

  const healthColor = HEALTH_COLORS[flagData.health_label] || "#94a3b8";

  if (compact) {
    return (
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid={`flags-compact-${projectId}`}>
        <div className="flex items-center gap-2">
          {flagData.critical_count > 0 && (
            <span className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded">
              <AlertCircle size={9}/> {flagData.critical_count} Critical
            </span>
          )}
          {flagData.warning_count > 0 && (
            <span className="flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
              <AlertTriangle size={9}/> {flagData.warning_count} Flags
            </span>
          )}
          <span className="text-xs font-bold" style={{ color: healthColor }}>
            Health: {flagData.health_score}% — {flagData.health_label}
          </span>
        </div>
        {expanded ? <ChevronUp size={11} className="text-slate-400"/> : <ChevronDown size={11} className="text-slate-400"/>}
      </button>
    );
  }

  return (
    <div data-testid={`flags-panel-${projectId}`}>
      {/* Health header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold" style={{ color: healthColor }}>Operational Health: {flagData.health_score}%</span>
          <span className="text-xs px-2 py-0.5 rounded font-bold" style={{ background: healthColor+"18", color: healthColor }}>{flagData.health_label}</span>
        </div>
        <span className="text-xs text-slate-400">{flagData.flag_count} flag{flagData.flag_count!==1?"s":""}</span>
      </div>

      <div className="space-y-2">
        {flagData.flags.map((flag, i) => {
          const sty = SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.info;
          const { icon: Icon } = sty;
          return (
            <div key={i} className="rounded-lg p-3 border" style={{ background: sty.bg, borderColor: sty.border }}
              data-testid={`flag-${flag.code}`}>
              <div className="flex items-start gap-2">
                <Icon size={12} style={{ color: sty.color, flexShrink: 0, marginTop: 1 }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold" style={{ color: sty.color }}>{sty.label}</span>
                    <span className="text-xs text-slate-500 font-mono">{flag.code}</span>
                  </div>
                  <p className="text-xs text-slate-700 mb-1">{flag.message}</p>
                  <p className="text-xs text-blue-600 font-medium">→ {flag.action}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Inline flag badge — compact version for tables.
 */
export function FlagsBadge({ projectId }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    client.get(`/lcfpi/operational-flags/${projectId}`)
      .then(r => setSummary(r.data)).catch(() => {});
  }, [projectId]);
  if (!summary) return null;
  if (summary.flag_count === 0) return <CheckCircle2 size={10} className="text-green-500" title="No operational flags" />;
  const hasC = summary.critical_count > 0;
  return (
    <span className="flex items-center gap-1 text-xs font-bold" style={{ color: hasC?"#ef4444":"#d97706" }}
      title={`${summary.critical_count} critical, ${summary.warning_count} warnings`}>
      {hasC ? <AlertCircle size={10}/> : <AlertTriangle size={10}/>}
      {summary.flag_count}
    </span>
  );
}

/**
 * Benchmarking panel for Finance Readiness page.
 */
export function BenchmarkingPanel({ projectId }) {
  const [bench, setBench] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!projectId) return;
    client.get(`/lcfpi/benchmarking/${projectId}`)
      .then(r => setBench(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);
  if (loading) return <div className="flex items-center gap-2 py-2"><Loader size={11} className="animate-spin text-slate-300"/><span className="text-xs text-slate-400">Loading benchmarks…</span></div>;
  if (!bench) return null;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="benchmarking-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-slate-700">Sector Benchmarking</p>
        {bench.top_quartile && <span className="text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">Top Quartile</span>}
        {bench.above_sector_average && !bench.top_quartile && <span className="text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded">Above Average</span>}
      </div>
      <p className="text-xs text-slate-600 mb-3 leading-relaxed">{bench.competitive_intelligence}</p>
      <div className="grid grid-cols-3 gap-2">
        {[
          ["Sector Percentile", `${bench.readiness_percentile_in_sector}th`, "#1e40af"],
          ["Global Percentile", `${bench.global_readiness_percentile}th`, "#6366f1"],
          ["Sector Peers", `${bench.sector_peers}`, "#064740b"],
          ["Sector Avg", `${bench.sector_avg_readiness}/100`, "#d97706"],
          ["Ranking", bench.ranking_label?.split(" ").slice(0,3).join(" "), "#059669"],
        ].map(([l,v,c])=>(
          <div key={l} className="bg-white rounded p-2 border border-slate-100">
            <p className="text-xs text-slate-400">{l}</p>
            <p className="text-xs font-black" style={{color:c}}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default OperationalFlagsPanel;
